import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Briefcase,
  FolderOpen,
  Users,
  UserCheck,
  UserX,
  Percent,
  BarChart3,
  Globe2,
  Building2,
  Home,
  FileEdit,
  TrendingUp
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";
import Loader from "../../../shared/components/Loader";
import JobDashboardClient, {
  CompanyJobMetrics,
  BaseMetricsAgg,
} from "../data/api/JobDashboardClient";
import "../JobDashboard.css";

const COLORS = ["#6366f1", "#06b6d4", "#f59e0b", "#ec4899", "#8b5cf6", "#10b981", "#ef4444"];

const shortId = (id: string | null | undefined) => {
  if (!id) return "—";
  return id.length > 12 ? id.slice(0, 12) + "…" : id;
};

const JobDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [companyMetrics, setCompanyMetrics] = useState<CompanyJobMetrics | null>(null);
  const [activeView, setActiveView] = useState<"global" | "internal" | "organization">("global");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const comp = await JobDashboardClient.getMetrics();
        if (!cancelled) {
          setCompanyMetrics(comp);
        }
      } catch (err: any) {
        if (!cancelled) setError("Failed to load dashboard data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const currentMetrics = useMemo((): BaseMetricsAgg | null => {
    if (activeView === "global") return companyMetrics;
    if (activeView === "internal") return companyMetrics?.internal_metrics || null;
    if (activeView === "organization") {
      if (!companyMetrics?.organizations_metrics) return null;
      const orgs = Object.values(companyMetrics.organizations_metrics);
      if (orgs.length === 0) return null;
      const agg: BaseMetricsAgg = {
        total_opportunities: 0, open_opportunities: 0, draft_opportunities: 0,
        total_applications: 0, total_hires: 0, total_rejected: 0, total_scored: 0,
        source_counts: {}, stage_counts: {}, monthly_applications: {}, action_required: {}
      };
      let sumScore = 0;
      let sumScored = 0;
      for (const o of orgs) {
        agg.total_opportunities += o.total_opportunities || 0;
        agg.open_opportunities += o.open_opportunities || 0;
        agg.draft_opportunities += o.draft_opportunities || 0;
        agg.total_applications += o.total_applications || 0;
        agg.total_hires += o.total_hires || 0;
        agg.total_rejected += o.total_rejected || 0;
        agg.total_scored += o.total_scored || 0;
        Object.entries(o.source_counts || {}).forEach(([k, v]) => agg.source_counts[k] = (agg.source_counts[k] || 0) + v);
        Object.entries(o.stage_counts || {}).forEach(([k, v]) => agg.stage_counts[k] = (agg.stage_counts[k] || 0) + v);
        Object.entries(o.monthly_applications || {}).forEach(([k, v]) => agg.monthly_applications[k] = (agg.monthly_applications[k] || 0) + v);
        if (o.avg_match_score && o.total_scored) {
          sumScore += (o.avg_match_score * o.total_scored);
          sumScored += o.total_scored;
        }
      }
      if (sumScored > 0) agg.avg_match_score = Math.round((sumScore / sumScored) * 100) / 100;
      return agg;
    }
    return null;
  }, [activeView, companyMetrics]);

  // Derived charts data
  const trendData = useMemo(() => {
    if (!currentMetrics?.monthly_applications) return [];
    return Object.entries(currentMetrics.monthly_applications)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([month, count]) => ({
        month,
        Applications: count,
      }));
  }, [currentMetrics]);

  const sourceData = useMemo(() => {
    if (!currentMetrics?.source_counts) return [];
    return Object.entries(currentMetrics.source_counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [currentMetrics]);

  // Stage distribution
  const aggregatedStages = useMemo(() => {
    if (!currentMetrics) return [];
    let totals: Record<string, number> = {};
    if (activeView === "global" && currentMetrics.stage_counts) {
      totals = { ...currentMetrics.stage_counts };
    } else if (companyMetrics && activeView !== "global") {
      const opps = currentMetrics === companyMetrics.internal_metrics 
        ? companyMetrics.per_opportunity.filter(o => o.opportunity_id) // Add internal check if needed
        : companyMetrics.per_opportunity;
      for (const opp of opps || []) {
        for (const [stage, count] of Object.entries(opp.stage_counts || {})) {
          totals[stage] = (totals[stage] || 0) + count;
        }
      }
    }
    
    // Backend now returns zero-filled stages, but we can maintain a preferred display order
    const stageOrder = ["Applied", "Shortlisted", "Interviewed", "Offered", "Accepted", "Rejected"];
    const orderedEntries = stageOrder.map(s => [s, totals[s] || 0] as [string, number]);
    const maxVal = Math.max(...orderedEntries.map(e => e[1]), 1);
    
    return orderedEntries.map(([stage, count], i) => ({
      stage,
      count,
      pct: Math.round((count / maxVal) * 100),
      color: COLORS[i % COLORS.length],
    }));
  }, [currentMetrics, companyMetrics, activeView]);

  return (
    <motion.div
      className="jd-page-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Loader isLoading={loading} message="Loading advanced analytics…" />

      {/* Glassy Header & Tabs */}
      <div className="jd-header-glass">
        <div className="jd-header-titles">
          <h1>Analytics Overview</h1>
          <p>Real-time hiring metrics and platform insights.</p>
        </div>
        <div className="jd-tabs">
          <button
            className={`jd-tab ${activeView === "global" ? "active" : ""}`}
            onClick={() => setActiveView("global")}
          >
            <Globe2 size={16} /> Global System
          </button>
          <button
            className={`jd-tab ${activeView === "internal" ? "active" : ""}`}
            onClick={() => setActiveView("internal")}
          >
            <Home size={16} /> Internal Hiring
          </button>
          <button
            className={`jd-tab ${activeView === "organization" ? "active" : ""}`}
            onClick={() => setActiveView("organization")}
          >
            <Building2 size={16} /> Client Organizations
          </button>
        </div>
      </div>

      {error && <div className="jd-error-banner">{error}</div>}

      {!loading && (!currentMetrics || currentMetrics.total_opportunities === 0) && (
        <div className="jd-empty-glass">
          <BarChart3 size={48} color="#94a3b8" />
          <span>No job data available for this view.</span>
        </div>
      )}

      <AnimatePresence mode="wait">
        {!loading && currentMetrics && currentMetrics.total_opportunities > 0 && (
          <motion.div
            key={activeView}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {/* KPI Grid */}
            <div className="jd-stats-grid">
              <StatCard icon={<Briefcase size={18} />} label="Total Opportunities" value={currentMetrics.total_opportunities || 0} />
              <StatCard icon={<FolderOpen size={18} />} label="Open Opportunities" value={currentMetrics.open_opportunities || 0} accent="blue" />
              <StatCard icon={<FileEdit size={18} />} label="Draft Opportunities" value={currentMetrics.draft_opportunities || 0} accent="orange" />
              <StatCard icon={<Users size={18} />} label="Total Applications" value={currentMetrics.total_applications || 0} />
              <StatCard icon={<UserCheck size={18} />} label="Total Hires" value={currentMetrics.total_hires || 0} accent="green" />
            </div>

            {/* Charts Row */}
            <div className="jd-charts-row">
              <div className="jd-chart-glass">
                <h2 className="jd-section-title">Applications Over Time</h2>
                <div className="jd-chart-wrapper">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="month" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                      <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                      <RechartsTooltip 
                        contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}
                      />
                      <Line type="monotone" dataKey="Applications" stroke="#3b82f6" strokeWidth={3} dot={{ fill: "#3b82f6", strokeWidth: 2, r: 4 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="jd-chart-glass">
                <h2 className="jd-section-title">Candidate Sources</h2>
                <div className="jd-chart-wrapper">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={sourceData}
                        cx="50%"
                        cy="50%"
                        innerRadius={65}
                        outerRadius={95}
                        paddingAngle={5}
                        dataKey="value"
                        stroke="none"
                      >
                        {sourceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip 
                        contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                      />
                      <Legend verticalAlign="bottom" height={36} iconType="circle" />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Stage distribution bar chart (Pipeline Funnel) */}
            {aggregatedStages.length > 0 && (
              <div className="jd-section-glass">
                <h2 className="jd-section-title">
                  {activeView === "global" ? "Global Pipeline" : "Overall Stage Distribution"}
                </h2>
                <div className="jd-bar-chart">
                  {aggregatedStages.map((s) => (
                    <div className="jd-bar-row" key={s.stage}>
                      <span className="jd-bar-label">{s.stage}</span>
                      <div className="jd-bar-track">
                        <motion.div
                          className="jd-bar-fill"
                          style={{ background: s.color }}
                          initial={{ width: 0 }}
                          animate={{ width: `${s.pct}%` }}
                          transition={{ duration: 0.8, type: "spring", bounce: 0.2 }}
                        >
                          {s.count > 0 ? s.count : ""}
                        </motion.div>
                      </div>
                      <span className="jd-bar-count">{s.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Required (Alerts) */}
            <div className="jd-section-glass">
              <h2 className="jd-section-title">Action Required</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ padding: '16px', background: 'rgba(254, 226, 226, 0.5)', borderLeft: '4px solid #ef4444', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <TrendingUp size={24} color="#ef4444" />
                  <div>
                    <h4 style={{ margin: 0, color: '#991b1b', fontSize: '14px', fontWeight: 600 }}>{currentMetrics?.action_required?.offers_expired || 0} offers expired</h4>
                    <p style={{ margin: '4px 0 0 0', color: '#b91c1c', fontSize: '12px' }}>Candidates haven't responded in 7+ days</p>
                  </div>
                </div>
                <div style={{ padding: '16px', background: 'rgba(254, 243, 199, 0.5)', borderLeft: '4px solid #f59e0b', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <TrendingUp size={24} color="#f59e0b" />
                  <div>
                    <h4 style={{ margin: 0, color: '#b45309', fontSize: '14px', fontWeight: 600 }}>{currentMetrics?.action_required?.interviews_awaiting_feedback || 0} interviews awaiting feedback</h4>
                    <p style={{ margin: '4px 0 0 0', color: '#d97706', fontSize: '12px' }}>Panel input overdue by 2+ days</p>
                  </div>
                </div>
                <div style={{ padding: '16px', background: 'rgba(254, 243, 199, 0.5)', borderLeft: '4px solid #f59e0b', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FolderOpen size={24} color="#f59e0b" />
                  <div>
                    <h4 style={{ margin: 0, color: '#b45309', fontSize: '14px', fontWeight: 600 }}>{currentMetrics?.action_required?.jobs_pending_approval || 0} jobs pending approval</h4>
                    <p style={{ margin: '4px 0 0 0', color: '#d97706', fontSize: '12px' }}>Waiting on department head sign-off</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Per-opportunity table (Only Company view) */}
            {activeView !== "global" && companyMetrics?.per_opportunity && companyMetrics.per_opportunity.length > 0 && (
              <div className="jd-section-glass">
                <h2 className="jd-section-title">Per-Opportunity Breakdown</h2>
                <div className="jd-table-wrap">
                  <table className="jd-table">
                    <thead>
                      <tr>
                        <th>Opportunity</th>
                        <th>Applications</th>
                        <th>Hired</th>
                        <th>Rejected</th>
                        <th>Match %</th>
                        <th>Stages</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companyMetrics.per_opportunity.map((opp) => {
                        const stages = Object.entries(opp.stage_counts || {}).sort(
                          (a, b) => b[1] - a[1]
                        );
                        return (
                          <tr
                            key={opp.opportunity_id || opp.company_id}
                            onClick={() => {
                              if (opp.opportunity_id) {
                                navigate(
                                  `/dashboard/job-opportunities/edit/${opp.opportunity_id}`
                                );
                              }
                            }}
                          >
                            <td>
                              <div className="jd-id-cell" title={opp.opportunity_id || ""}>
                                {shortId(opp.opportunity_id)}
                              </div>
                            </td>
                            <td>{opp.total_applications}</td>
                            <td>{opp.hired_count}</td>
                            <td>{opp.rejected_count}</td>
                            <td>
                              {opp.avg_match_score != null
                                ? `${Math.round(opp.avg_match_score * 100)}%`
                                : "—"}
                            </td>
                            <td>
                              <div className="jd-chips">
                                {stages.map(([stage, count]) => (
                                  <span className="jd-chip" key={stage}>
                                    {stage}: <span className="jd-chip-count">{count}</span>
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ── Inline stat card component ── */
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  accent?: "green" | "red" | "blue" | "orange" | "purple";
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, accent }) => (
  <motion.div
    className={`jd-stat-card${accent ? ` jd-stat-${accent}` : ""}`}
    whileHover={{ y: -5, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)" }}
    transition={{ duration: 0.2 }}
  >
    <div className="jd-stat-header">
      {icon}
      <span className="jd-stat-label">{label}</span>
    </div>
    <div className="jd-stat-value">{value}</div>
  </motion.div>
);

export default JobDashboardPage;
